
import { Component, Prop, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { DateFormatMixin, HeMixin } from "@/mixins";
import { Technician } from "@/store/modules";
import TechnicianChangeCognitoButton from "./TechnicianChangeCognitoButton.vue";
import AuthRegisterButton from "./AuthRegisterButton.vue";
import ChangePasswordButton from "./ChangePasswordButton.vue";
import EditEmailModal from "../../tech-app-users/EditEmailModal.vue";
import { authUserService, AuthUserResponse } from "@/services/AuthUserService";
import { userManagementService } from "@/services/UserManagementService";

@Component({
  components: {
    TechnicianChangeCognitoButton,
    AuthRegisterButton,
    ChangePasswordButton,
    EditEmailModal
  },
})
export class TechnicianCardInfo extends mixins(DateFormatMixin, HeMixin) {
  @Prop(Technician)
  private technician!: Technician;

  // Email editing properties
  showEditEmailModal = false;
  showEmailSuccessBanner = false;
  emailUpdateLoading = false;

  // User info properties
  userInfo: AuthUserResponse | null = null;
  loadingUserInfo = false;
  actionLoading = false;

  async mounted() {
    if (this.technician.cognito_email) {
      await this.loadUserInfo();
    }
  }

  async loadUserInfo() {
    if (!this.technician.cognito_email) return;
    
    this.loadingUserInfo = true;
    try {
      const response = await authUserService.getUserInfo(this.technician.cognito_email);
      if (response.data && response.data.status === 'success') {
        this.userInfo = response.data;
      }
    } catch (error) {
      console.error('Error loading user info:', error);
    } finally {
      this.loadingUserInfo = false;
    }
  }

  getEnabledColor(enabled: boolean): string {
    return enabled ? "success" : "danger";
  }

  getEnabledText(enabled: boolean): string {
    return enabled ? "Yes" : "No";
  }

  getStatusColor(status: string): string {
    if (!status) return "secondary";
    
    switch (status.toLowerCase()) {
      case "confirmed":
        return "success";
      case "unconfirmed":
        return "warning";
      case "force_change_password":
        return "info";
      case "reset_required":
        return "warning";
      case "admin_create_user":
        return "info";
      default:
        return "secondary";
    }
  }

  async handleEnabledClick() {
    if (!this.userInfo || !this.technician.cognito_email) return;

    const isCurrentlyEnabled = this.userInfo.user.enabled;
    const action = isCurrentlyEnabled ? 'disable' : 'enable';
    const actionText = isCurrentlyEnabled ? 'disable access' : 'enable access';
    
    const confirmed = await this.showConfirmationDialog(
      `Are you sure you want to ${actionText} of this user?`,
      `Yes, ${action} access`,
      'Cancel'
    );

    if (confirmed) {
      this.actionLoading = true;
      
      try {
        if (action === 'enable') {
          await userManagementService.enableUser(this.technician.cognito_email);
          this.$root.$bvToast.toast('User access enabled successfully', {
            title: 'Success',
            variant: 'success'
          });
        } else if (action === 'disable') {
          await userManagementService.disableUser(this.technician.cognito_email);
          this.$root.$bvToast.toast('User access disabled successfully', {
            title: 'Success',
            variant: 'success'
          });
        }
        
        // Reload user info to reflect changes
        await this.loadUserInfo();
        
      } catch (error) {
        console.error('Error toggling user access:', error);
        this.$root.$bvToast.toast('Failed to update user access. Please try again.', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.actionLoading = false;
      }
    }
  }

  handleEditEmail() {
    this.showEmailSuccessBanner = false;
    this.showEditEmailModal = true;
  }

  handleCancelEditEmail() {
    this.showEditEmailModal = false;
    this.showEmailSuccessBanner = false;
  }

  async handleSaveEmail(payload: { currentEmail: string; newEmail: string }) {
    this.emailUpdateLoading = true;
    
    try {
      const response = await userManagementService.updateEmail(payload.currentEmail, payload.newEmail);
      
      if (response.data && response.data.status === 'success') {
        this.$root.$bvToast.toast(response.data.message || 'User email updated successfully', {
          title: 'Success',
          variant: 'success'
        });
        
        this.showEmailSuccessBanner = true;
        
        setTimeout(async () => {
          this.showEditEmailModal = false;
          this.showEmailSuccessBanner = false;
          // Refresh technician data to get updated email
          this.onSave(this.technician.ID);
        }, 2000);
      } else {
        const errorMessage = response.data?.message || 'Failed to update user email';
        this.$root.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger'
        });
      }
      
    } catch (error) {
      console.error('Error updating user email:', error);
      
      let errorMessage = 'Failed to update user email. Please try again.';
      
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;
        
        if (responseData && responseData.message) {
          errorMessage = responseData.message;
        } else if (status === 409) {
          errorMessage = 'An account with this email already exists';
        } else if (status === 400) {
          errorMessage = 'Invalid email format or request';
        } else if (status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      this.$root.$bvToast.toast(errorMessage, {
        title: 'Error',
        variant: 'danger'
      });
    } finally {
      this.emailUpdateLoading = false;
    }
  }

  private showConfirmationDialog(message: string, confirmText: string, cancelText: string): Promise<boolean> {
    return new Promise((resolve) => {
      const result = confirm(message);
      resolve(result);
    });
  }

  onSave(val: number) {
    this.$emit('on-saved', val);
  }
}

export default TechnicianCardInfo;
